import type { TelefonieSpurStufe } from "./ticketTypen";

/**
 * Telefonie-Spuren fürs Ticket (T-28): Ringpuffer der letzten 30
 * Konsolenmeldungen, deren erstes Argument mit „[telephony]" beginnt — egal ob
 * error/warn/info/log/debug. Die normalen Ticket-Spuren (ticketSpuren.ts) sehen
 * nur console.error; die Telefonie (linkusSdk.ts, TelephonyContext.tsx) loggt
 * aber fast alles per warn/debug, und genau das fehlte in T-28.
 *
 * Bewusst ohne weitere Abhängigkeiten: ticketSpuren.ts installiert den Puffer
 * mit, und utils/api.ts hängt an ticketSpuren.ts — ein Import von linkusSdk
 * hier ergäbe einen Import-Zyklus.
 */
const MAX = 30;
const MAX_TEXT = 500;
const MAX_ARG = 300;
const PRAEFIX = "[telephony]";
const STUFEN: TelefonieSpurStufe[] = ["error", "warn", "info", "log", "debug"];

type Spur = { stufe: TelefonieSpurStufe; zeit: string; text: string };
let puffer: Spur[] = [];
let installiert = false;

const argText = (a: unknown): string => {
  if (a instanceof Error) return `${a.name}: ${a.message}`;
  if (typeof a === "string") return a;
  if (a === undefined) return "undefined";
  try {
    const j = JSON.stringify(a);
    const t = j === undefined ? String(a) : j;
    return t.length > MAX_ARG ? `${t.slice(0, MAX_ARG)}…` : t;
  } catch {
    return String(a);
  }
};

/** true, wenn die Meldung zur Telefonie gehört (erstes Argument beginnt mit „[telephony]"). */
export function istTelefonieMeldung(args: unknown[]): boolean {
  return typeof args[0] === "string" && args[0].startsWith(PRAEFIX);
}

/** Merkt eine Konsolenmeldung, falls sie zur Telefonie gehört. */
export function merkeTelefonieMeldung(stufe: TelefonieSpurStufe, args: unknown[]): void {
  if (!istTelefonieMeldung(args)) return;
  const text = args.map(argText).join(" ").slice(0, MAX_TEXT);
  puffer.push({ stufe, zeit: new Date().toISOString(), text });
  if (puffer.length > MAX) puffer = puffer.slice(-MAX);
}

export function telefonieSpurenSchnappschuss(): Spur[] {
  return puffer.map((s) => ({ ...s }));
}

export function _telefonieSpurenLeeren(): void {
  puffer = [];
}

/** Hängt sich an alle fünf Konsolen-Stufen; stört das Original-Logging nie. */
export function installiereTelefonieSpuren(): void {
  if (installiert || typeof console === "undefined") return;
  installiert = true;
  for (const stufe of STUFEN) {
    const original = console[stufe];
    if (typeof original !== "function") continue;
    const gebunden = original.bind(console);
    console[stufe] = (...args: unknown[]) => {
      try {
        merkeTelefonieMeldung(stufe, args);
      } catch {
        /* Spuren dürfen Logging nie stören */
      }
      gebunden(...args);
    };
  }
}
